import React, { useRef } from 'react';
import { cx } from '@emotion/css';

import { DISTANCE_UNIT } from '../../../constants/ecp-locator';
import { useStyles } from './store-info.styles';
import { StoreInfoProps } from './store-info.models';
import { useStoreByType } from '../../../hooks/use-store-by-type';
import { Icon, IconSizes, IconTypes } from '../icon';
import { isVendingMachine, roundRating } from '../../../utils/ecp-locator';
import { getRoundDistance } from '../../../utils/distance';
import { formatPhoneNumber } from '../../../utils';

export const StoreInfo = ({
  name,
  rating,
  type,
  distance,
  street,
  openinghours,
  phonenumber,
  detailedLocation,
  contentStyles,
  contentPartialStyles,
  titleStyles,
}: StoreInfoProps): JSX.Element => {
  const classes = useStyles();

  const phoneEl = useRef<HTMLAnchorElement>(null);
  const titleEl = useRef<HTMLHeadingElement>(null);

  const address = isVendingMachine(type) && detailedLocation?.length
    ? `${street}, ${detailedLocation}`
    : street;

  return (
    <>
      <h2
        data-testid="store-title" // Used by YM
        className={cx(titleStyles || classes.title, 'title-bold')}
        ref={titleEl}
        tabIndex={-1}
      >
        {name}
      </h2>
      <div className={contentStyles || classes.content}>
        <div className={contentPartialStyles}>
          { /* Used by YM */ }
          <p data-testid="store-rating-bar" className={classes.ratingWrapper}>
            {rating && (
              <span
                data-testid="store-rating" // Used by YM
                className={cx(classes.rating, classes.pointSeparator)}
              >
                {roundRating(rating)}
              </span>
            )}
            <span
              data-testid="store-type" // Used by YM
              className={classes.pointSeparator}
            >
              {useStoreByType(type)}
            </span>
            {distance && (
              // Used by YM
              <span data-testid="store-distance">
                {`${getRoundDistance(distance)} ${DISTANCE_UNIT}`}
              </span>
            )}
          </p>
          { /* Used by YM */ }
          <p data-testid="store-address" className={cx(classes.street, classes.iconWrapper)}>
            <span className={classes.icon}>{street && <Icon type={IconTypes.mapMarkerBlack} size={IconSizes.xsm} aria-hidden="true" />}</span>
            {address}
          </p>
        </div>
        <div className={contentPartialStyles}>
          <p
            data-testid="store-hours" // Used by YM
            className={cx(classes.hours, classes.iconWrapper)}
          >
            <span className={classes.icon}>{openinghours && (
              <Icon type={IconTypes.clockBlack} size={IconSizes.xs} aria-hidden="true" />
            )}
            </span>
            <span>{openinghours}</span>
          </p>
          {phonenumber && (
            <span className={classes.iconWrapper}>
            <span className={classes.icon}>{<Icon type={IconTypes.phoneBlack} size={IconSizes.xs} aria-hidden="true" />}</span>
            <a
              ref={phoneEl}
              data-testid="store-phone" // Used by YM
              href={`tel:${phonenumber}`}
              className={classes.phone}
            >
              {formatPhoneNumber(phonenumber)}
            </a>
          </span>
          )}
        </div>
      </div>
    </>
  );
};
